import i18n from 'config/i18n';
import { pick } from 'ramda';

import { Arrangement } from '../../../types';

import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import { getLocale } from 'utils/date';
import dayjs from 'dayjs';
import { GridColDef } from '@mui/x-data-grid-pro';
import { nestedFieldValueGetter } from 'utils/grid';

type Row = {
  flags: string[];
  [key: string]: any;
};

const t = i18n.getFixedT(getLocale());

export const travelLinesTableColumDefinitions: GridColDef[] = [
  {
    field: 'distanceRangeFrom',
    headerName: t('adminPortal.arrangements.details.from'),
    flex: 1
  },
  {
    field: 'distanceRangeTo',
    headerName: t('adminPortal.arrangements.details.to'),
    flex: 1
  },
  {
    field: 'expensesPerDistance',
    headerName: t('adminPortal.arrangements.details.perKm'),
    flex: 1
  },
  {
    field: 'expensesNominal',
    headerName: t('adminPortal.arrangements.details.nominal'),
    flex: 1
  },
  {
    field: 'expensesMax',
    headerName: t('adminPortal.arrangements.details.max'),
    flex: 1
  },
  {
    headerName: t('validFrom'),
    field: 'valid.from',
    flex: 1,
    type: 'date',
    valueFormatter: dateFormatter,
    valueGetter: (_, row: Row) => nestedFieldValueGetter('valid.from', row)
  },
  {
    headerName: t('validTo'),
    field: 'valid.to',
    flex: 1,
    type: 'date',
    valueFormatter: dateFormatter,
    valueGetter: (_, row: Row) => nestedFieldValueGetter('valid.to', row)
  }
];

export const transformTravelSetLinesToRequest = (travelSetLines) =>
  travelSetLines.map((travelSetLine) => ({
    ...(travelSetLine.trvSetId && {
      id: travelSetLine.trvSetId
    }),
    ...travelSetLine
  }));

const arrangementProps = [
  'id',
  'code',
  'externalCode',
  'description',
  'compensationTypeId',
  'isExternal'
];

const travelSetLineProps = [
  'expensesNominal',
  'distanceRangeFrom',
  'distanceRangeTo',
  'expensesPerDistance',
  'expensesMax',
  'validFrom',
  'validTo'
];

export const transformArrangementToCreateRequest = (arrangement) => {
  return {
    ...pick(arrangementProps, arrangement),
    ...(arrangement?.codeExtern && { externalCode: arrangement.codeExtern }),
    ...(arrangement?.transportationMode && {
      transportationMode: arrangement?.transportationMode
    }),
    travelSetLine: {
      ...pick(travelSetLineProps, arrangement.travelSetLine),
      ...(dayjs(arrangement?.travelSetLine?.from).isValid() && {
        validFrom: dayjs(arrangement.travelSetLine.from).format('YYYY-MM-DD')
      }),
      ...(dayjs(arrangement?.travelSetLine?.to).isValid() && {
        validTo: dayjs(arrangement.travelSetLine.to).format('YYYY-MM-DD')
      })
    }
  };
};

export const transformArrangementToEditRequest = (arrangement: Arrangement) => {
  return {
    ...pick(arrangementProps, arrangement),
    ...(arrangement?.codeExtern && { externalCode: arrangement.codeExtern }),
    ...(arrangement?.transportationMode && {
      transportationMode: arrangement?.transportationMode
    }),
    ...(arrangement.travelSetLines && {
      travelSetLines: arrangement.travelSetLines.map((elem) => ({
        ...pick(travelSetLineProps, elem),
        ...(elem?.trvSetLineId && { id: elem.trvSetLineId }),
        ...(elem?.valid?.from && {
          validFrom: dayjs(elem?.valid?.from).format('YYYY-MM-DD')
        }),
        ...(elem?.valid?.to && {
          validTo: dayjs(elem?.valid?.to).format('YYYY-MM-DD')
        })
      }))
    })
  };
};

export const isUniqueArrangementCode = (
  newCode: string,
  oldCode: string,
  arrangements: readonly Arrangement[] = []
) => {
  return (
    !arrangements.some(
      (arrangements: Arrangement) => arrangements.code === newCode
    ) || newCode === oldCode
  );
};
